import React from 'react';
import { Alert, Button, Space } from 'antd';
import { CheckCircleOutlined, DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  enrollmentConflictsInboxPath,
  enrollmentPartialApplySummary,
  enrollmentPausedMessage,
  resolvePlainOrgCode,
} from '../enrollmentConflictHelpers';

/**
 * Banner when deals upload or staff approve still has enrollment conflicts.
 * Clean / identical rows may already be applied or dismissed (tiered partial apply).
 */
export default function EnrollmentPausedBanner({
  data = {},
  socialNo,
  onDownload,
  downloading = false,
  style,
}) {
  const navigate = useNavigate();
  const sessionId = data.session_id;
  const { onboarded, updated, dismissed, needs, held } = enrollmentPartialApplySummary(data);
  const partiallyApplied = onboarded > 0 || updated > 0 || dismissed > 0
    || String(data.status || '').toLowerCase() === 'partially_applied';
  // Prefer API plain social_no; never pass corp-route base64 hash into holds filters.
  const plainSocialNo = resolvePlainOrgCode(data.social_no, socialNo);

  return (
    <Alert
      type={partiallyApplied ? 'info' : 'warning'}
      showIcon
      icon={partiallyApplied ? <CheckCircleOutlined /> : <WarningOutlined />}
      style={style}
      message={partiallyApplied ? enrollmentPausedMessage(data) : 'Enrollment needs resolution — conflicts must be cleared'}
      description={
        <div>
          {!partiallyApplied ? (
            <p style={{ marginBottom: 8 }}>{enrollmentPausedMessage(data)}</p>
          ) : null}
          <p style={{ marginBottom: 12, color: '#64748b', fontSize: 13 }}>
            {onboarded > 0 ? `${onboarded} on the onboarding list · ` : ''}
            {updated > 0 ? `${updated} updated · ` : ''}
            {dismissed > 0 ? `${dismissed} dismissed as same · ` : ''}
            {needs} open conflict{needs === 1 ? '' : 's'}
            {held > 0 ? ` · ${held} clean still held` : ''}
            {sessionId ? ` · session #${sessionId}` : ''}
            {' · '}already-onboarded rows are live; only remaining conflicts need review
          </p>
          <Space wrap>
            <Button
              type="primary"
              size="small"
              onClick={() => navigate(enrollmentConflictsInboxPath({
                sessionId,
                socialNo: plainSocialNo,
              }))}
            >
              Open enrollment holds
            </Button>
            {typeof onDownload === 'function' ? (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={onDownload}
              >
                Download CSV
              </Button>
            ) : null}
          </Space>
        </div>
      }
    />
  );
}
